import { MutableRefObject, useContext, useMemo } from "react";
import { Controller } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { Button, Col, Form, Input, Row, Select, Space, Tag } from "antd";
import {
    ArrowLeftOutlined,
    ArrowRightOutlined,
    CalendarOutlined,
    EditOutlined,
    InfoCircleOutlined,
    SaveOutlined,
    SettingOutlined,
} from "@ant-design/icons";

import { IGiveaway } from "../../../../../../interfaces";
import { ThemeContext } from "../../../../../../contexts/ThemeContext";
import DatePicker from "../../../../../../components/DatePicker";
import useGiveawayDataStep from "./useGiveawayDataStep";
import { DateFns } from "../../../../../../utils/dateFns";
import useAuth from "../../../../../../hooks/useAuth";
import { EGiveawayStatus } from "../../../../../../enums";

interface IProps {
    giveaway?: IGiveaway;
    onSubmit: (user: IGiveaway) => Promise<void>;
    onSubmitGiveawayDataStepFinished: () => void;
    refIsEditing: MutableRefObject<boolean>;
    canEdit: boolean;
}

export default function GiveawayDataStep({
    onSubmit,
    giveaway,
    onSubmitGiveawayDataStepFinished,
    refIsEditing,
    canEdit,
}: IProps) {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === 'dark';
    const navigate = useNavigate();

    const {
        isSubmiting,
        handleSubmit,
        handleSubmitForm,
        control,
        errors,
        handleChangeDateRange,
        handleClickUpdateAndLeave,
        getMinimumGiveawayDate,
        watch,
        setValue,
        raffleDelayDays
    } = useGiveawayDataStep({ onSubmit, giveaway, onSubmitGiveawayDataStepFinished });

    const { user } = useAuth();

    const isAdmin = useMemo(() => {
        return user?.role?.id === 1;
    }, [user]);

    const sectionTitleStyle: React.CSSProperties = {
        fontSize: 13,
        fontWeight: 600,
        color: isDark ? '#94a3b8' : '#64748b',
        textTransform: 'uppercase',
        letterSpacing: '0.4px',
        marginBottom: 12,
        display: 'flex',
        alignItems: 'center',
        gap: 6,
    };

    return (
        <Form
            onFinish={handleSubmit(handleSubmitForm)}
            autoComplete="off"
            layout="vertical"
        >
            <div
                style={{
                    background: isDark ? '#141414' : '#ffffff',
                    borderRadius: 10,
                    border: `1px solid ${isDark ? '#262626' : '#eaedf1'}`,
                    boxShadow: isDark
                        ? '0 4px 20px rgba(0, 0, 0, 0.35)'
                        : '0 2px 10px rgba(0, 0, 0, 0.03)',
                    padding: '24px',
                }}
            >
                {/* Seção 1: Identificação & Regras */}
                <div style={{ marginBottom: 20 }}>
                    <div style={sectionTitleStyle}>
                        <InfoCircleOutlined style={{ color: '#1677ff' }} />
                        <span>Identificação e Regras Gerais</span>
                    </div>

                    <Row gutter={[12, 12]}>
                        <Col xs={24} md={12} lg={12}>
                            <Form.Item
                                label="Descrição do Sorteio"
                                style={{ marginBottom: 0 }}
                                validateStatus={errors.description ? 'error' : undefined}
                                help={errors.description?.message}
                            >
                                <Controller
                                    name="description"
                                    control={control}
                                    rules={{ required: { value: true, message: 'Por favor, digite a descrição do sorteio!' } }}
                                    render={({ field }) => (
                                        <Input
                                            {...field}
                                            prefix={<EditOutlined style={{ color: isDark ? '#666' : '#bfbfbf' }} />}
                                            placeholder="Ex: Sorteio de Natal 2026 / Edição Especial"
                                            disabled={refIsEditing.current && !canEdit}
                                        />
                                    )}
                                />
                            </Form.Item>
                        </Col>

                        <Col xs={24} sm={12} md={6} lg={6}>
                            <Form.Item
                                label="Modo do Sorteio"
                                style={{ marginBottom: 0 }}
                                validateStatus={errors.mode ? 'error' : undefined}
                                help={errors.mode?.message}
                                tooltip="Sorteio especial considera todos os bilhetes emitidos no ano atual."
                            >
                                <Controller
                                    name="mode"
                                    control={control}
                                    rules={{ required: { value: true, message: 'Por favor, selecione o modo do sorteio!' } }}
                                    render={({ field }) => (
                                        <Select
                                            {...field}
                                            placeholder="Selecione o modo"
                                            defaultValue="normal"
                                            style={{ width: '100%' }}
                                            onChange={(value) => {
                                                field.onChange(value);
                                                setValue('date', '');

                                                if (value === 'especial') {
                                                    const currentYear = new Date().getFullYear();
                                                    setValue('period_beginning', String(new Date(currentYear, 0, 1)));
                                                    setValue('period_ending', String(new Date(currentYear, 11, 31)));
                                                }
                                            }}
                                            options={[
                                                { value: 'normal', label: 'Normal (Período definido)' },
                                                { value: 'especial', label: 'Especial (Anual)' },
                                            ]}
                                            disabled={refIsEditing.current}
                                        />
                                    )}
                                />
                            </Form.Item>
                        </Col>

                        <Col xs={24} sm={12} md={6} lg={6}>
                            <Form.Item
                                label="Tipo de Apuração"
                                style={{ marginBottom: 0 }}
                                validateStatus={errors.type ? 'error' : undefined}
                                help={errors.type?.message}
                                tooltip="Identifica se a apuração é baseada na extração da Loteria Federal ou geração interna do sistema."
                            >
                                <Controller
                                    name="type"
                                    control={control}
                                    rules={{ required: { value: true, message: 'Por favor, selecione o tipo do sorteio!' } }}
                                    render={({ field }) => (
                                        <Select
                                            {...field}
                                            placeholder="Selecione o tipo"
                                            defaultValue="Loteria Federal"
                                            style={{ width: '100%' }}
                                            onChange={(value) => {
                                                field.onChange(value);
                                                setValue('date', '');
                                            }}
                                            options={[
                                                { value: 'Loteria Federal', label: 'Loteria Federal' },
                                                { value: 'Interno', label: 'Interno (Sistema)' },
                                            ]}
                                            disabled={refIsEditing.current && !canEdit}
                                        />
                                    )}
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                </div>

                {/* Seção 2: Período & Cronograma */}
                <div style={{ marginBottom: 20 }}>
                    <div style={sectionTitleStyle}>
                        <CalendarOutlined style={{ color: '#722ed1' }} />
                        <span>Período e Cronograma</span>
                    </div>

                    <Row gutter={[12, 12]}>
                        <Col xs={24} md={8}>
                            <Form.Item
                                label="Período de Participação"
                                style={{ marginBottom: 0 }}
                                validateStatus={errors.period_beginning || errors.period_ending ? 'error' : undefined}
                                help={errors.period_beginning?.message || errors.period_ending?.message}
                                tooltip="Intervalo de datas em que as notas fiscais emitidas serão elegíveis para geração de bilhetes."
                            >
                                <Controller
                                    name="period_beginning"
                                    control={control}
                                    rules={{
                                        required: {
                                            value: true,
                                            message: "Por favor, informe o período do sorteio!"
                                        },
                                    }}
                                    render={({ field: beginningField }) => (
                                        <Controller
                                            name="period_ending"
                                            control={control}
                                            render={({ field: endingField }) => (
                                                <DatePicker.RangePicker
                                                    format={(date) => date.toLocaleDateString('PT-BR')}
                                                    value={[
                                                        beginningField.value ? new Date(beginningField.value) : null,
                                                        endingField.value ? new Date(endingField.value) : null
                                                    ]}
                                                    onChange={handleChangeDateRange}
                                                    style={{ width: '100%' }}
                                                    disabled={isAdmin ? false : refIsEditing.current}
                                                    allowClear
                                                />
                                            )}
                                        />
                                    )}
                                />
                            </Form.Item>
                        </Col>

                        <Col xs={24} md={8}>
                            <Form.Item
                                label="Data Limite de Importação"
                                style={{ marginBottom: 0 }}
                                validateStatus={errors.import_deadline ? 'error' : undefined}
                                help={errors.import_deadline?.message}
                                tooltip="Prazo máximo para importação/lançamento de notas no sistema antes da apuração."
                            >
                                <Controller
                                    name="import_deadline"
                                    control={control}
                                    rules={{
                                        required: {
                                            value: true,
                                            message: "Por favor, informe a data limite para importação das notas!"
                                        }
                                    }}
                                    render={({ field }) => (
                                        <DatePicker
                                            format={(date) => date.toLocaleDateString('PT-BR', { hour: '2-digit', minute: '2-digit' })}
                                            value={field.value ? new Date(field.value) : undefined}
                                            onChange={field.onChange}
                                            style={{ width: '100%' }}
                                            disabled={refIsEditing.current && !canEdit}
                                            showTime={{
                                                minuteStep: 30,
                                                format: 'HH:mm',
                                                showSecond: false,
                                                defaultValue: DateFns.setTimeToSixPM(new Date()),
                                            }}
                                            disabledDate={(currentDate) => {
                                                if (!currentDate) return false;

                                                const periodEnding = watch('period_ending');

                                                if (periodEnding) {
                                                    const end = new Date(periodEnding);
                                                    end.setHours(0, 0, 0, 0);

                                                    const selected = new Date(currentDate);
                                                    selected.setHours(0, 0, 0, 0);

                                                    if (selected <= end) {
                                                        return true;
                                                    }
                                                }

                                                return false;
                                            }}
                                        />
                                    )}
                                />
                            </Form.Item>
                        </Col>

                        <Col xs={24} md={8}>
                            <Form.Item
                                label={
                                    <span>
                                        Data do Sorteio
                                        {raffleDelayDays > 0 && (
                                            <Tag color="warning" style={{ marginLeft: 6, fontSize: 11, borderRadius: 4 }}>
                                                Adiado em {raffleDelayDays} dia(s)
                                            </Tag>
                                        )}
                                    </span>
                                }
                                style={{ marginBottom: 0 }}
                                validateStatus={errors.date ? 'error' : undefined}
                                help={errors.date?.message}
                            >
                                <Controller
                                    name="date"
                                    control={control}
                                    rules={{
                                        required: {
                                            value: true,
                                            message: "Por favor, informe a data do sorteio!"
                                        }
                                    }}
                                    render={({ field }) => (
                                        <DatePicker
                                            format={(date) => date.toLocaleDateString('PT-BR', { hour: '2-digit', minute: '2-digit' })}
                                            value={field.value ? new Date(field.value) : undefined}
                                            onChange={(date) => {
                                                if (date && watch('type') === 'Loteria Federal') {
                                                    date = DateFns.setHour(date, 19);
                                                }
                                                field.onChange(date);
                                            }}
                                            style={{ width: '100%' }}
                                            disabled={refIsEditing.current && !canEdit}
                                            disabledDate={(currentDate) => {
                                                if (!currentDate) return false;

                                                const selectedDate = new Date(currentDate);
                                                selectedDate.setHours(0, 0, 0, 0);

                                                const minDate = getMinimumGiveawayDate(watch("import_deadline"));

                                                if (minDate) {
                                                    minDate.setHours(0, 0, 0, 0);

                                                    if (selectedDate < minDate) {
                                                        return true;
                                                    }
                                                }

                                                if (watch("type") !== "Interno") {
                                                    const day = currentDate.getDay();
                                                    return day !== 3 && day !== 0;
                                                }

                                                return false;
                                            }}
                                            disabledTime={(currentDate) => {
                                                const importDeadline = watch('import_deadline');

                                                if (!currentDate || !importDeadline) {
                                                    return {};
                                                }

                                                const deadline = new Date(importDeadline);

                                                const sameDay =
                                                    currentDate.getFullYear() === deadline.getFullYear() &&
                                                    currentDate.getMonth() === deadline.getMonth() &&
                                                    currentDate.getDate() === deadline.getDate();

                                                if (!sameDay) {
                                                    return {};
                                                }

                                                const minDate = new Date(deadline);
                                                minDate.setMinutes(minDate.getMinutes() + 30);

                                                const minHour = minDate.getHours();
                                                const minMinute = minDate.getMinutes();

                                                return {
                                                    disabledHours: () =>
                                                        Array.from({ length: 24 }, (_, i) => i).filter(
                                                            (hour) => hour < minHour
                                                        ),

                                                    disabledMinutes: (selectedHour) => {
                                                        if (selectedHour !== minHour) {
                                                            return [];
                                                        }

                                                        return Array.from({ length: 60 }, (_, i) => i).filter(
                                                            (minute) => minute < minMinute
                                                        );
                                                    },
                                                };
                                            }}
                                            showTime={watch('type') !== 'Loteria Federal' && ({
                                                minuteStep: 30,
                                                format: 'HH:mm',
                                                showSecond: false,
                                                defaultValue: DateFns.setTimeToSixPM(new Date()),
                                            })}
                                        />
                                    )}
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                </div>

                {/* Seção 3: Status Operacional (apenas administradores) */}
                {isAdmin && (
                    <div style={{ marginBottom: 20 }}>
                        <div style={sectionTitleStyle}>
                            <SettingOutlined style={{ color: '#fa8c16' }} />
                            <span>Status Operacional</span>
                        </div>

                        <Row gutter={[12, 12]}>
                            <Col xs={24} sm={12} md={8}>
                                <Form.Item
                                    label="Status do Sorteio"
                                    style={{ marginBottom: 0 }}
                                    validateStatus={errors.status ? 'error' : undefined}
                                    help={errors.status?.message}
                                    tooltip="Status administrativo do sorteio."
                                >
                                    <Controller
                                        name="status"
                                        control={control}
                                        rules={{ required: { value: true, message: 'Por favor, selecione o status do sorteio!' } }}
                                        render={({ field }) => (
                                            <Select
                                                {...field}
                                                placeholder="Selecione o status"
                                                defaultValue={EGiveawayStatus.ON_HOLD}
                                                style={{ width: '100%' }}
                                                onChange={(value) => {
                                                    field.onChange(value);
                                                    setValue('status', value as EGiveawayStatus);
                                                }}
                                                options={[
                                                    { value: EGiveawayStatus.ON_HOLD, label: 'Em espera' },
                                                    { value: EGiveawayStatus.FORCE, label: 'Vigente' },
                                                    { value: EGiveawayStatus.IMPORT_PERIOD, label: 'Período de importação' },
                                                ]}
                                                disabled={refIsEditing.current && !canEdit}
                                            />
                                        )}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                    </div>
                )}

                {/* Barra de Ações Inferior */}
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: 12,
                        paddingTop: 16,
                        borderTop: `1px solid ${isDark ? '#262626' : '#f0f2f5'}`,
                    }}
                >
                    <Button
                        icon={<ArrowLeftOutlined />}
                        onClick={() => navigate('/sorteios/geral')}
                        style={{
                            borderRadius: 8,
                            height: 38,
                            padding: '0 16px',
                            fontWeight: 500,
                        }}
                    >
                        Voltar para Sorteios
                    </Button>

                    <Space size={10}>
                        {giveaway && (
                            <Button
                                type="default"
                                htmlType="button"
                                icon={<SaveOutlined />}
                                disabled={isSubmiting || !canEdit}
                                onClick={handleSubmit(handleClickUpdateAndLeave)}
                                style={{
                                    height: 38,
                                    borderRadius: 8,
                                    padding: '0 16px',
                                    fontWeight: 500,
                                }}
                            >
                                Salvar e Sair
                            </Button>
                        )}

                        <Button
                            type="primary"
                            htmlType="submit"
                            icon={<ArrowRightOutlined />}
                            loading={isSubmiting}
                            disabled={isSubmiting || (giveaway && !canEdit)}
                            style={{
                                height: 38,
                                borderRadius: 8,
                                fontWeight: 600,
                                padding: '0 20px',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 6,
                            }}
                        >
                            Avançar para Prêmios
                        </Button>
                    </Space>
                </div>
            </div>
        </Form>
    );
}
